import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { CheckCircle, Clock, ArrowRight, Home, BookOpen, Mail, Calendar } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paymentId = searchParams.get('payment_id');
  const intentId = searchParams.get('intent_id');
  const isManual = searchParams.get('manual');
  const [status, setStatus] = useState('loading');
  const [payment, setPayment] = useState(null);

  useEffect(() => {
    if (isManual) {
      setStatus('pending');
      return;
    }

    const pollStatus = async () => {
      try {
        let res;
        
        if (paymentId) {
          // Airwallex payment
          res = await axios.get(`${API}/payments/airwallex/status/${paymentId}`);
          setPayment(res.data.payment);
          
          if (res.data.status === 'paid' || res.data.payment?.payment_status === 'paid') {
            setStatus('success');
          } else if (res.data.status === 'failed' || res.data.status === 'cancelled') {
            setStatus('error');
          } else {
            setStatus('processing');
            setTimeout(pollStatus, 3000);
          }
        } else if (sessionId) {
          // Stripe payment
          res = await axios.get(`${API}/payments/status/${sessionId}`);
          setPayment(res.data.payment);
          
          if (res.data.payment_status === 'paid') {
            setStatus('success');
          } else if (res.data.status === 'expired') {
            setStatus('expired');
          } else {
            setStatus('processing');
            setTimeout(pollStatus, 2000);
          }
        } else {
          setStatus('success'); // For mock payments
        }
      } catch {
        setStatus('error');
      }
    };
    
    pollStatus();
  }, [sessionId, paymentId, isManual]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-SG', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="py-16 md:py-24 min-h-[70vh] flex items-center">
      <div className="container mx-auto px-4 md:px-6 max-w-xl">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-8 md:p-12 text-center"
        >
          {/* Success State */}
          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h1 className="font-outfit text-3xl font-bold text-white mb-4" data-testid="payment-success-title">
                Payment Successful!
              </h1>
              <p className="text-slate-400 mb-6">
                Your enrollment has been successfully confirmed.
              </p>
              
              {payment && (
                <div className="bg-surface-navy/50 rounded-xl p-5 mb-8 text-left space-y-4">
                  {payment.course_name && (
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-5 h-5 text-electric-cyan flex-shrink-0" />
                      <div>
                        <p className="text-slate-400 text-sm">Course</p>
                        <p className="text-white">{payment.course_name}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3">
                    <span className="w-5 h-5 text-electric-cyan flex-shrink-0 font-bold">$</span>
                    <div>
                      <p className="text-slate-400 text-sm">Amount Paid</p>
                      <p className="text-white">{payment.currency || 'SGD'} {payment.amount}</p>
                    </div>
                  </div>

                  {payment.student_email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-electric-cyan flex-shrink-0" />
                      <div>
                        <p className="text-slate-400 text-sm">Email</p>
                        <p className="text-white">{payment.student_email}</p>
                      </div>
                    </div>
                  )}

                  {payment.paid_at && (
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-electric-cyan flex-shrink-0" />
                      <div>
                        <p className="text-slate-400 text-sm">Payment Date</p>
                        <p className="text-white">{formatDate(payment.paid_at)}</p>
                      </div>
                    </div>
                  )}

                  <div className="pt-3 border-t border-surface-navy">
                    <p className="text-slate-500 text-xs">
                      Transaction ID: {payment.airwallex_transaction_id || payment.session_id || payment.id}
                    </p>
                  </div>
                </div>
              )}
              
              <p className="text-slate-400 text-sm mb-8">
                A confirmation email has been sent. Our team will contact you with your course schedule.
              </p>
            </>
          )}

          {/* Pending State (Manual Payment) */}
          {status === 'pending' && (
            <>
              <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-yellow-400" />
              </div>
              <h1 className="font-outfit text-3xl font-bold text-white mb-4" data-testid="payment-pending-title">
                Payment Submitted
              </h1>
              <p className="text-slate-400 mb-8">
                Your payment is being verified. We'll confirm within 24 hours.
              </p>
            </>
          )}

          {/* Processing State */}
          {status === 'processing' && (
            <>
              <div className="w-20 h-20 rounded-full bg-electric-cyan/20 flex items-center justify-center mx-auto mb-6">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-electric-cyan"></div>
              </div>
              <h1 className="font-outfit text-3xl font-bold text-white mb-4">
                Processing Payment...
              </h1>
              <p className="text-slate-400 mb-8">
                Please wait while we confirm your payment.
              </p>
            </>
          )}

          {/* Loading State */}
          {status === 'loading' && (
            <>
              <div className="w-20 h-20 rounded-full bg-surface-navy flex items-center justify-center mx-auto mb-6">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-electric-cyan"></div>
              </div>
              <h1 className="font-outfit text-3xl font-bold text-white mb-4">
                Checking Payment Status...
              </h1>
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                <span className="text-red-400 text-4xl">!</span>
              </div>
              <h1 className="font-outfit text-3xl font-bold text-white mb-4">
                Unable to Verify Payment
              </h1>
              <p className="text-slate-400 mb-8">
                Please contact support if you believe this is an error.
              </p>
            </>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/" className="btn-secondary flex items-center justify-center gap-2">
              <Home className="w-4 h-4" /> Back to Home
            </Link>
            <Link to="/courses" className="btn-primary flex items-center justify-center gap-2">
              View Courses <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
